import cuid from "cuid";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT } from "utils/constants";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import Button from "../Components/Button";
import Card from "../Components/Card";
import Footer from "../Components/Footer";
import Header from "../Components/Header";
import Input from "../Components/Input";
import validation from "./validation";

function View({
  contract,
  ria,
  onSubmit,
  onBack,
  deltas,
  locked,
  fields,
  terminationRequest,
  onEffectiveDateChange,
  priceLoading,
  modalVisible,
  setFieldValueRef,
  initialContract,
}) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: {
      contract,
      ria,
      locked,
      fields,
      terminationRequest,
    },
    onSubmit: onSubmit,
    validateOnMount: true,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        deltaRia: get(deltas, "start.ria"),
        deltaStart: get(deltas, "start.normal"),
        deltaEndRia: get(deltas, "end.ria"),
        deltaEnd: get(deltas, "end.normal"),
      },
    ),
  });
  const isValid = useMemo(() => {
    const projectRia = values?.ria;
    const selfOccupation = values?.fields?.selfOccupation;
    const contractNumber = values?.terminationRequest?.contractNumber;
    const currentCompany =
      values?.terminationRequest?.brokerageMandate?.currentCompany;

    return (
      !projectRia ||
      (projectRia && selfOccupation) ||
      (projectRia && !isEmpty(contractNumber) && !isEmpty(currentCompany))
    );
  }, [
    values?.fields?.selfOccupation,
    values?.ria,
    values?.terminationRequest?.brokerageMandate?.currentCompany,
    values?.terminationRequest?.contractNumber,
  ]);

  // Initialize with the initial contract date or the current values
  const initialDate = useMemo(() => {
    return (
      get(values, "contract.issuanceDate") ||
      get(contract, "issuanceDate") ||
      get(initialContract, "issuanceDate") ||
      null
    );
  }, [values, contract, initialContract]);

  const previousDateRef = useRef(initialDate);
  const isProcessingRef = useRef(false);
  const pendingDateRef = useRef(null);
  const onEffectiveDateChangeRef = useRef(onEffectiveDateChange);

  // Update the ref when the function changes
  useEffect(() => {
    onEffectiveDateChangeRef.current = onEffectiveDateChange;
  }, [onEffectiveDateChange]);

  // Store setFieldValue in ref if provided
  useEffect(() => {
    if (setFieldValueRef) {
      setFieldValueRef.current = setFieldValue;
    }
  }, [setFieldValue, setFieldValueRef]);

  // Separate validation effect
  useEffect(() => {
    formProps.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.contract?.issuanceDate]);

  // Check for year changes separately
  useEffect(() => {
    // Skip if we're already processing a change or if function is not provided
    if (isProcessingRef.current || !onEffectiveDateChangeRef.current) {
      return;
    }

    const newDate = values?.contract?.issuanceDate;
    const previousDate = previousDateRef.current;

    // Helper function to normalize dates for comparison (handles dayjs, moment, strings, etc.)
    const normalizeDate = (date) => {
      if (!date) return null;
      // Check if it's already a dayjs/moment object with .format() method
      if (typeof date.format === "function") {
        return date.format();
      }
      // Otherwise, convert to moment and format
      const m = moment(date);
      return m.isValid() ? m.format() : null;
    };

    // Normalize dates for comparison
    const normalizedNewDate = normalizeDate(newDate);
    const normalizedPreviousDate = normalizeDate(previousDate);

    // If we have a pending date and the new date matches the previous (revert case), clear pending
    if (
      pendingDateRef.current &&
      normalizedNewDate === normalizedPreviousDate
    ) {
      pendingDateRef.current = null;
      return;
    }
    // Skip if date hasn't actually changed
    if (!newDate || normalizedNewDate === normalizedPreviousDate) {
      return;
    }
    // Helper function to get year from date (handles dayjs, moment, strings, etc.)
    const getYear = (date) => {
      if (!date) return null;
      // Check if it's already a dayjs/moment object with .year() method
      if (typeof date.year === "function") {
        return date.year();
      }
      // Otherwise, convert to moment
      const m = moment(date);
      return m.isValid() ? m.year() : null;
    };

    // Helper function to check if date is valid
    const isValidDate = (date) => {
      if (!date) return false;
      // Check if it's already a dayjs/moment object
      if (typeof date.isValid === "function") {
        return date.isValid();
      }
      return moment(date).isValid();
    };

    // Only proceed if date is valid
    if (!isValidDate(newDate)) {
      return;
    }

    const newYear = getYear(newDate);

    let previousYear = null;
    let baseDateForComparison = null;

    // Determine the previous date/year to compare with
    if (previousDate && isValidDate(previousDate)) {
      // Use the previous ref date
      previousYear = getYear(previousDate);
      baseDateForComparison = previousDate;
    } else {
      // No previous date, use the initial contract date or current year
      const baseDate = initialDate;
      if (baseDate && isValidDate(baseDate)) {
        previousYear = getYear(baseDate);
        baseDateForComparison = baseDate;
      } else {
        // No initial date, use current year
        previousYear = moment().year();
        baseDateForComparison = moment().toISOString();
      }
    }
    // Check if year changed
    if (newYear !== null && previousYear !== null && newYear !== previousYear) {
      // Year changed - trigger price recalculation
      isProcessingRef.current = true;
      pendingDateRef.current = newDate;
      const baseDate =
        baseDateForComparison || initialDate || moment().toISOString();

      const promise = onEffectiveDateChangeRef.current(
        newDate,
        setFieldValue,
        baseDate,
      );

      // Handle both sync and async functions
      if (promise && typeof promise.then === "function") {
        promise
          .then(() => {
            isProcessingRef.current = false;
            // Only update if still pending (not reverted)
            if (pendingDateRef.current === newDate) {
              previousDateRef.current = newDate;
              pendingDateRef.current = null;
            } else {
              pendingDateRef.current = null;
            }
          })
          .catch((error) => {
            console.error("Error in price change calculation:", error);
            isProcessingRef.current = false;
            pendingDateRef.current = null;
            // Don't update previousDateRef on error - keep the original date
            // This ensures that if the user changes the date again, it will compare correctly
          });
      } else {
        // Sync function, update ref immediately if still pending
        isProcessingRef.current = false;
        if (pendingDateRef.current === newDate) {
          previousDateRef.current = newDate;
          pendingDateRef.current = null;
        }
      }
    } else {
      // Year didn't change, just update the ref
      previousDateRef.current = newDate;
      pendingDateRef.current = null;
    }
  }, [values?.contract?.issuanceDate, setFieldValue, initialDate]);

  return (
    <div className="solyon">
      <div className="solyon__container">
        <div className="solyon__wrapper">
          <Header
            title="À partir de quelle date souhaitez-vous être couvert ?"
            highlight="souhaitez-vous être,couvert ?"
            subtitle="Souscription : Date d’effet"
          />
          <div className="solyon__body">
            <div>
              <div className="solyon__body--inputs">
                <Input
                  width={isMobile ? "100%" : 316}
                  type="date"
                  format={DATE_FORMAT}
                  placeholder={"JJ / MM / AAAA"}
                  label={"Date d'effet"}
                  disabledDate={(current) => current < moment()}
                  disabled={priceLoading}
                  {...bindDateInputProps({
                    name: "contract.issuanceDate",
                    setFieldValue,
                    values,
                    ...formProps,
                  })}
                  touched
                />
                {priceLoading && (
                  <div style={{ marginTop: 8, color: "#666", fontSize: 14 }}>
                    Calcul des prix en cours...
                  </div>
                )}
                <Input
                  type="checkbox"
                  label={
                    "Cochez cette case si vous souhaitez résilier un contrat existant souscrit depuis plus d’un an."
                  }
                  checked={get(values, "ria") || false}
                  onChange={({ target: { checked } }) =>
                    setFieldValue("ria", checked)
                  }
                />
                {values?.ria && (
                  <Card>
                    <div className="grid--1">
                      <Input
                        type="radio"
                        label="Comment souhaitez-vous gérer la résiliation de votre ancien contrat ?"
                        values={[
                          {
                            value: true,
                            label: "Je me charge de résilier moi-même.",
                          },
                          {
                            value: false,
                            label:
                              "Je souhaite que So’Lyon Mutuelle s'en occupe pour moi.",
                          },
                        ]}
                        defaultValue={get(values, "fields.selfOccupation")}
                        onChange={(value) => {
                          setFieldValue("fields.selfOccupation", value);
                          if (!value) {
                            setFieldValue(
                              "contract.issuanceDate",
                              moment(get(values, "contract.issuanceDate"))
                                .add(deltas.start.ria, "days")
                                .toISOString(),
                            );
                          }
                          setFieldValue(
                            "terminationRequest",
                            value
                              ? null
                              : {
                                  id: cuid(),
                                  brokerageMandate: { id: cuid() },
                                },
                          );
                        }}
                      />
                      {get(values, "fields.selfOccupation") === false && (
                        <>
                          <Input
                            label="N° de référence de votre contrat"
                            placeholder="ex : 123456789"
                            {...bindInputProps({
                              name: "terminationRequest.contractNumber",
                              values,
                              ...formProps,
                            })}
                          />
                          <Input
                            label="Nom de mon assureur ou de ma mutuelle"
                            placeholder="ex : ABC Mutuelle"
                            {...bindInputProps({
                              name: "terminationRequest.brokerageMandate.currentCompany",
                              values,
                              ...formProps,
                            })}
                          />
                        </>
                      )}
                    </div>
                  </Card>
                )}
              </div>
            </div>
            <div className="solyon__body--actions">
              <Button
                type={"primary"}
                alignment={"center"}
                disabled={
                  !isEmpty(formProps.errors) ||
                  !isValid ||
                  priceLoading ||
                  modalVisible
                }
                onClick={handleSubmit}
              >
                <div className="d-flex justify--center">Continuer</div>
              </Button>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
}

export default View;
